import React, {FC, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {Alert, Divider, Typography} from "antd";
import dayjs from "dayjs";
import {CheckboxElement, DropdownElement, TextFieldElement} from "@ib-tehnologije/react-widgets";
import {RenderAntdGraphQLList} from "../../../Components/AdminModalForm";
import {PageHeader, Panel} from "../../../Components/ui";
import {
    EventFeeTypeCreateDtoInput,
    EventPricePeriodCreateDtoInput,
    useCreateEventFeePriceMutation,
    useCreateEventFeeTypeMutation,
    useCreateEventPricePeriodMutation,
    useEventFeePricesListQuery,
    useEventPricePeriodsListQuery,
    useUpdateEventFeePriceMutation,
    useUpdateEventFeeTypeMutation,
    useUpdateEventPricePeriodMutation
} from "../../../GraphQL/generated/graphql";
import {formatLocalDate} from "../adminDateFormat";
import {
    buildDefaultPricePeriodFormData,
    buildFeeTypeCreateInput,
    buildFeeTypeUpdateInput,
    buildPricePeriodInput,
    joinPrerequisiteLabels,
    pricePeriodDisplayName
} from "./eventConfigPayloads";
import {useEventBasicSetup, useEventConfigScope, useEventFeeTypesQuery} from "./useEventConfigScope";

const {Title} = Typography;

const pricePeriodFormDefinition = () => ({
    name: {translationKey: "name", type: new TextFieldElement({})},
    startDate: {translationKey: "start-date", type: new TextFieldElement({inputProps: {type: "date"}})},
    endDate: {translationKey: "end-date", type: new TextFieldElement({inputProps: {type: "date"}})}
});

const feeTypeFormDefinition = () => ({
    labelHr: {translationKey: "label-hr", type: new TextFieldElement({})},
    labelEn: {translationKey: "label-en", type: new TextFieldElement({})},
    isPublicRegistrationHidden: {
        translationKey: "fee-type-public-hidden",
        type: new CheckboxElement({})
    },
    requiresParticipantTypeProof: {
        translationKey: "fee-type-requires-proof",
        type: new CheckboxElement({})
    }
});

// Event workspace "registration-fees": pricing periods, participant fee types and fee rows.
const EventFeesPage: FC = () => {
    const {t} = useTranslation();
    const scope = useEventConfigScope();
    const {eventNumericId, baseWhere} = scope;
    const {event: selectedBasicSetupEvent} = useEventBasicSetup(eventNumericId);
    const eventHasParticipationFee = selectedBasicSetupEvent?.hasParticipationFee ?? true;
    const pricePeriodValidationLabels = useMemo(() => ({
        startDateRequired: t("price-period-start-date-required", {defaultValue: "Start date is required."}),
        endDateRequired: t("price-period-end-date-required", {defaultValue: "End date is required."}),
        endBeforeStart: t("price-period-end-before-start", {defaultValue: "End date must be on or after start date."}),
        overlap: (period: string, startDate: string, endDate: string) => t("price-period-overlap", {
            defaultValue: "Pricing periods cannot overlap. This range overlaps with {{period}} ({{startDate}} - {{endDate}}).",
            endDate,
            period,
            startDate
        })
    }), [t]);

    const pricePeriodsQuery = useEventPricePeriodsListQuery({variables: {take: 50, where: baseWhere()}});
    const feeTypesQuery = useEventFeeTypesQuery(scope);
    const feePriceWhere = useMemo(() => {
        if (!eventNumericId) {
            return undefined;
        }

        const feeTypeIds = (feeTypesQuery.data?.eventFeeTypes?.items ?? []).map(ft => ft.id);
        const pricePeriodIds = (pricePeriodsQuery.data?.eventPricePeriods?.items ?? []).map(pp => pp.id);

        return {
            and: [
                {feeTypeId: {in: feeTypeIds.length ? feeTypeIds : [-1]}},
                {pricePeriodId: {in: pricePeriodIds.length ? pricePeriodIds : [-1]}}
            ]
        };
    }, [eventNumericId, feeTypesQuery.data?.eventFeeTypes?.items, pricePeriodsQuery.data?.eventPricePeriods?.items]);
    const feePricesQuery = useEventFeePricesListQuery({
        variables: {
            take: 50,
            where: feePriceWhere
        }
    });

    const [createPricePeriod] = useCreateEventPricePeriodMutation();
    const [updatePricePeriod] = useUpdateEventPricePeriodMutation();
    const [createFeeType] = useCreateEventFeeTypeMutation();
    const [updateFeeType] = useUpdateEventFeeTypeMutation();
    const [createFeePrice] = useCreateEventFeePriceMutation();
    const [updateFeePrice] = useUpdateEventFeePriceMutation();

    const pricePeriodBaseFormData = useMemo(() => buildDefaultPricePeriodFormData({
        eventId: eventNumericId,
        eventStartDate: selectedBasicSetupEvent?.startDate,
        eventEndDate: selectedBasicSetupEvent?.endDate
    }), [eventNumericId, selectedBasicSetupEvent?.endDate, selectedBasicSetupEvent?.startDate]);

    const feeTypeOptions = useMemo(
        () => (feeTypesQuery.data?.eventFeeTypes?.items ?? []).map(ft => ({id: ft.id, label: ft.labelHr ?? ft.labelEn})),
        [feeTypesQuery.data?.eventFeeTypes?.items]
    );
    const pricePeriodOptions = useMemo(
        () => (pricePeriodsQuery.data?.eventPricePeriods?.items ?? []).map(pp => ({id: pp.id, label: pricePeriodDisplayName(pp)})),
        [pricePeriodsQuery.data?.eventPricePeriods?.items]
    );
    const hasFeeTypeOptions = feeTypeOptions.length > 0;
    const hasPricePeriodOptions = pricePeriodOptions.length > 0;
    const canCreateFeePrices = hasFeeTypeOptions && hasPricePeriodOptions;
    const missingFeePricePrerequisites = [
        !hasFeeTypeOptions ? t("event-config-fee-price-requires-type", {defaultValue: "at least one participant fee type"}) : null,
        !hasPricePeriodOptions ? t("event-config-fee-price-requires-period", {defaultValue: "at least one pricing period"}) : null
    ].filter((value): value is string => !!value);
    const feePricePrerequisiteDescription = missingFeePricePrerequisites.length > 0
        ? t("event-config-fee-price-prerequisite-description", {
            defaultValue: "Add {{items}} before adding fee rows.",
            items: joinPrerequisiteLabels(missingFeePricePrerequisites)
        })
        : null;
    const feeTypeLabelsById = useMemo(() => {
        const map = new Map<number, string>();
        (feeTypesQuery.data?.eventFeeTypes?.items ?? []).forEach(ft => {
            if (ft) {
                map.set(ft.id, ft.labelHr ?? ft.labelEn ?? "");
            }
        });
        return map;
    }, [feeTypesQuery.data?.eventFeeTypes?.items]);
    const pricePeriodNamesById = useMemo(() => {
        const map = new Map<number, string>();
        (pricePeriodsQuery.data?.eventPricePeriods?.items ?? []).forEach(pp => {
            if (pp) {
                map.set(pp.id, pp.name);
            }
        });
        return map;
    }, [pricePeriodsQuery.data?.eventPricePeriods?.items]);
    const feePriceFormDefinition = () => ({
        feeTypeId: {translationKey: "fee-type", type: new DropdownElement({values: feeTypeOptions, valueKey: "id", titleKey: "label", showEmpty: false})},
        pricePeriodId: {translationKey: "period", type: new DropdownElement({values: pricePeriodOptions, valueKey: "id", titleKey: "label", showEmpty: false})},
        amount: {translationKey: "amount", type: new TextFieldElement({})}
    });

    return (
        <div className="ati-stack">
            <PageHeader level={2} title={t("setup-sequence.pricing-title", {defaultValue: "Fees"})}/>
            {!eventHasParticipationFee && (
                <Alert
                    type="success"
                    showIcon
                    message={t("setup-sequence.pricing-no-fee-title", {
                        defaultValue: "This event does not charge a participation fee"
                    })}
                    description={t("setup-sequence.pricing-no-fee-description", {
                        defaultValue: "You can leave fee periods, participant fee types, and fee rows empty. Return here only if the event starts charging fees."
                    })}
                />
            )}
            <Panel>
                <div className="ati-stack">
                    <div>
                        <Title level={5}>{t("pricing-periods", {defaultValue: "Pricing periods"})}</Title>
                        <RenderAntdGraphQLList
                            tableData={pricePeriodsQuery.data?.eventPricePeriods}
                            columns={[
                                {columnId: "name", title: t("name"), sorter: true, renderCell: r => r.name},
                                {columnId: "startDate", title: t("start-date"), sorter: true, renderCell: r => formatLocalDate(r.startDate)},
                                {columnId: "endDate", title: t("end-date"), sorter: true, renderCell: r => formatLocalDate(r.endDate)}
                            ]}
                            creating={{
                                fn: (options: any) => createPricePeriod(options as any),
                                modelKey: "input",
                                baseFormData: pricePeriodBaseFormData as EventPricePeriodCreateDtoInput,
                                formDefinition: pricePeriodFormDefinition(),
                                formDataToDto: values => buildPricePeriodInput(
                                    {...pricePeriodBaseFormData, ...values},
                                    eventNumericId,
                                    pricePeriodsQuery.data?.eventPricePeriods?.items ?? [],
                                    pricePeriodValidationLabels
                                )
                            }}
                            updating={{
                                fn: (options: any) => updatePricePeriod(options as any),
                                modelKey: "input",
                                formDefinition: pricePeriodFormDefinition(),
                                formDataToDto: values => buildPricePeriodInput(
                                    values,
                                    eventNumericId,
                                    pricePeriodsQuery.data?.eventPricePeriods?.items ?? [],
                                    pricePeriodValidationLabels,
                                    Number(values.id)
                                ),
                                selectionToBaseFormData: item => ({
                                    id: item?.id,
                                    eventId: item?.eventId,
                                    name: item?.name,
                                    code: item?.code,
                                    startDate: item?.startDate ? dayjs(item.startDate).format("YYYY-MM-DD") : undefined,
                                    endDate: item?.endDate ? dayjs(item.endDate).format("YYYY-MM-DD") : undefined
                                })
                            }}
                            loading={pricePeriodsQuery.loading}
                            getRowId={row => row.id}
                            getNotificationKey={row => `${row.name}`}
                            query={pricePeriodsQuery}
                            pageStorageKey={"event-price-periods"}
                        />
                    </div>
                    <Divider/>
                    <div>
                        <Title level={5}>{t("event-config-fee-types-guided", {defaultValue: "Participant fee types"})}</Title>
                        <RenderAntdGraphQLList
                            tableData={feeTypesQuery.data?.eventFeeTypes}
                            columns={[
                                {columnId: "labelHr", title: t("label-hr"), sorter: true, renderCell: r => r.labelHr},
                                {columnId: "labelEn", title: t("label-en"), sorter: true, renderCell: r => r.labelEn},
                                {
                                    columnId: "isPublicRegistrationHidden",
                                    title: t("fee-type-public-hidden", {defaultValue: "Hidden on public form"}),
                                    sorter: true,
                                    renderCell: r => r.isPublicRegistrationHidden ? t("yes", {defaultValue: "Yes"}) : t("no", {defaultValue: "No"})
                                },
                                {
                                    columnId: "requiresParticipantTypeProof",
                                    title: t("fee-type-requires-proof", {defaultValue: "Requires proof"}),
                                    sorter: true,
                                    renderCell: r => r.requiresParticipantTypeProof ? t("yes", {defaultValue: "Yes"}) : t("no", {defaultValue: "No"})
                                }
                            ]}
                            creating={{
                                fn: (options: any) => createFeeType(options as any),
                                modelKey: "input",
                                baseFormData: {
                                    eventId: eventNumericId,
                                    labelHr: "",
                                    labelEn: "",
                                    isFree: false,
                                    isSponsored: false,
                                    isPublicRegistrationHidden: false,
                                    requiresParticipantTypeProof: false
                                } as EventFeeTypeCreateDtoInput,
                                formDefinition: feeTypeFormDefinition(),
                                formDataToDto: values => buildFeeTypeCreateInput(values, eventNumericId)
                            }}
                            updating={{
                                fn: (options: any) => updateFeeType(options as any),
                                modelKey: "input",
                                formDefinition: feeTypeFormDefinition(),
                                formDataToDto: values => buildFeeTypeUpdateInput(
                                    values,
                                    eventNumericId,
                                    feeTypesQuery.data?.eventFeeTypes?.items ?? []
                                ),
                                selectionToBaseFormData: item => ({
                                    id: item?.id,
                                    eventId: item?.eventId,
                                    labelHr: item?.labelHr,
                                    labelEn: item?.labelEn,
                                    isFree: item?.isFree,
                                    isSponsored: item?.isSponsored,
                                    isPublicRegistrationHidden: item?.isPublicRegistrationHidden,
                                    requiresParticipantTypeProof: item?.requiresParticipantTypeProof
                                })
                            }}
                            loading={feeTypesQuery.loading}
                            getRowId={row => row.id}
                            getNotificationKey={row => `${row.labelHr ?? row.labelEn}`}
                            query={feeTypesQuery}
                            pageStorageKey={"event-fee-types"}
                        />
                    </div>
                    <Divider/>
                    <div>
                        <Title level={5}>{t("event-config-fee-prices-guided", {defaultValue: "Fee rows"})}</Title>
                        {feePricePrerequisiteDescription && (
                            <Alert
                                type="info"
                                showIcon
                                style={{margin: "12px 0"}}
                                message={t("event-config-fee-prices-prerequisite-title", {
                                    defaultValue: "Finish fee setup before adding fee rows."
                                })}
                                description={feePricePrerequisiteDescription}
                            />
                        )}
                        <RenderAntdGraphQLList
                            tableData={feePricesQuery.data?.eventFeePrices}
                            columns={[
                                {columnId: "feeTypeId", title: t("fee-type"), sorter: true, renderCell: r => feeTypeLabelsById.get(r.feeTypeId) ?? r.feeTypeId},
                                {columnId: "pricePeriodId", title: t("period"), sorter: true, renderCell: r => pricePeriodNamesById.get(r.pricePeriodId) ?? r.pricePeriodId},
                                {columnId: "amount", title: t("amount"), sorter: true, renderCell: r => r.amount}
                            ]}
                            createInlineButtonProps={{
                                type: "primary",
                                children: t("event-config-add-fee-row", {defaultValue: "Add fee row"}),
                                disabled: !canCreateFeePrices
                            }}
                            creating={{
                                fn: (options: any) => createFeePrice(options as any),
                                modelKey: "input",
                                baseFormData: {
                                    feeTypeId: feeTypeOptions[0]?.id ?? "",
                                    pricePeriodId: pricePeriodOptions[0]?.id ?? "",
                                    amount: 0
                                } as any,
                                formDefinition: feePriceFormDefinition(),
                                formDataToDto: values => ({
                                    feeTypeId: Number(values.feeTypeId),
                                    pricePeriodId: Number(values.pricePeriodId),
                                    amount: Number(values.amount)
                                })
                            }}
                            updating={{
                                fn: (options: any) => updateFeePrice(options as any),
                                modelKey: "input",
                                formDefinition: feePriceFormDefinition(),
                                formDataToDto: values => ({
                                    id: Number(values.id),
                                    feeTypeId: Number(values.feeTypeId),
                                    pricePeriodId: Number(values.pricePeriodId),
                                    amount: Number(values.amount)
                                }),
                                selectionToBaseFormData: item => ({
                                    id: item?.id,
                                    feeTypeId: item?.feeTypeId,
                                    pricePeriodId: item?.pricePeriodId,
                                    amount: item?.amount
                                })
                            }}
                            loading={feePricesQuery.loading}
                            getRowId={row => row.id}
                            getNotificationKey={row => `${feeTypeLabelsById.get(row.feeTypeId) ?? row.feeTypeId} ${pricePeriodNamesById.get(row.pricePeriodId) ?? row.pricePeriodId}`}
                            query={feePricesQuery}
                            pageStorageKey={"event-fee-prices"}
                        />
                    </div>
                </div>
            </Panel>
        </div>
    );
};

export default EventFeesPage;
